import { Dispatch } from "react";

import { logAnalyticsEvent } from "@/constants/amplitude";
import { Colors } from "@/constants/colors";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { Languages, mapLocalizedLanguage } from "@/constants/languages";
import { CheckoutContainersIds, PALTA_PAYMENTS_API, PAYMENT_METHODS } from "@/constants/payment";
import { PALTA_PAYMENTS_V2_API_KEY } from "@/constants/prices";
import { PaymentAction, PaymentActions } from "@/utils/payment/reducer";
import {
    CustomerId,
    PaymentClientSettingsType,
    showPaymentFormData,
} from "@palta-brain/web-sdk/dist/cjs/src/type";
import { PaymentFlow } from "@primer-io/checkout-web";

export const PAYMENT_WRAPPER_SELECTOR = "zing-payment-container";
export const PRIMER_CHECKOUT_CONTAINER = "primer-checkout-container";
export const PRIMER_CHECKOUT_CONTAINER_PAYPAL = "checkout-container-paypal";
export const PRIMER_CHECKOUT_CONTAINER_APPLE = "checkout-container-apple";
export const BRAINTREE_CHECKOUT_CONTAINER = "braintree-checkout-container";
export const BRAINTREE_CHECKOUT_CONTAINER_PAYPAL = "paypal-button";
export const BRAINTREE_CHECKOUT_CONTAINER_CARD = "card-container";
export const BRAINTREE_CHECKOUT_CONTAINER_CARD_NUMBER = "card-number";
export const BRAINTREE_CHECKOUT_CONTAINER_EXPIRATION_DATE = "expiration-date";
export const BRAINTREE_CHECKOUT_CONTAINER_CVV = "cvv";

const getMetadata = (isApplePayAvailable: boolean | null) => {
    let metadata: Record<string, number> = {
        paypal: 1,
        card: 1,
    };

    if (isApplePayAvailable) {
        metadata = {
            ...metadata,
            [PAYMENT_METHODS.APPLE]: 1,
        };
    }

    return metadata;
};

export const getClientSettings = (
    email: string,
    isApplePayAvailable: boolean | null,
    paywallName: string,
    dispatchPayment: Dispatch<PaymentAction>
): PaymentClientSettingsType => ({
    apiEndpoint: PALTA_PAYMENTS_API,
    apiKey: PALTA_PAYMENTS_V2_API_KEY,
    email: email,
    metadata: getMetadata(isApplePayAvailable),
    onError: (error, description) => {
        console.error(description);
        console.error(error);
        logAnalyticsEvent(WebEventTypes.PURCHASE_FAILED, {
            [WebEventProps.SCREEN]: paywallName,
            error,
            description,
        });
        dispatchPayment({
            type: PaymentActions.SET_PAYMENT_ERROR,
            isPaymentError: true,
        });
    },
    onPaymentStatusChange: (status, paymentMethodType) => {
        console.log(status, paymentMethodType);
        if (paymentMethodType) {
            dispatchPayment({
                type: PaymentActions.SET_PAYMENT_METHOD,
                paymentMethod: paymentMethodType as PAYMENT_METHODS,
            });
        }
        if (status === "FAIL" || status === "ERROR") {
            dispatchPayment({
                type: PaymentActions.SET_PAYMENT_PENDING,
                isPaymentPending: false,
            });
            dispatchPayment({
                type: PaymentActions.SET_PAYPAL_PAYMENT_LOADING,
                isPaypalPaymentLoading: false,
            });
        }
        if (status === "PENDING") {
            dispatchPayment({
                type: PaymentActions.SET_PAYMENT_PENDING,
                isPaymentPending: true,
            });
            dispatchPayment({
                type: PaymentActions.SET_PAYMENT_ERROR,
                isPaymentError: false,
            });

            if (paymentMethodType === "PAYPAL_BILLING_AGREEMENT") {
                dispatchPayment({
                    type: PaymentActions.SET_PAYPAL_PAYMENT_LOADING,
                    isPaypalPaymentLoading: true,
                });
            }
            logAnalyticsEvent(WebEventTypes.PURCHASE_STARTED, {
                [WebEventProps.SCREEN]: paywallName,
                paymentMethodType,
            });
        }
        if (status === "COMPLETE") {
            dispatchPayment({
                type: PaymentActions.SET_PAYMENT_COMPLETE,
                isPaymentComplete: true,
            });
        }
    },
});

export const getCustomerId = (uuid: string): CustomerId => ({
    type: "merchant-str",
    value: uuid,
});

export const getPrimerOptions = ({
    language,
    isApplePayAvailable,
    paywallName,
    dispatchPayment,
    isFemale,
}: {
    language: Languages;
    isFemale: boolean;
    isApplePayAvailable: boolean | null;
    paywallName: string;
    dispatchPayment: Dispatch<PaymentAction>;
}): showPaymentFormData["primerOption"] => ({
    container: PRIMER_CHECKOUT_CONTAINER,
    locale: mapLocalizedLanguage[language],
    vault: { visible: false },
    card: {
        cardholderName: {
            visible: false,
        },
    },
    style: {
        inputLabel: {
            fontFamily: "Outfit-Zing-VF",
            fontSize: "16px",
            color: isFemale ? "#797F91" : Colors.WHITE,
        },
        input: {
            base: {
                background: isFemale ? "#F3F4F7" : "transparent",
                height: 56,
                fontSize: "16px",
                lineHeight: "22px",
                borderStyle: "solid",
                borderColor: isFemale ? Colors.NEUTRAL_COOL_GREY_300 : Colors.NEUTRAL_COOL_GREY_700,
                borderWidth: 1,
                borderRadius: 8,
                fontWeight: "400",
                color: isFemale ? "#030D1B" : Colors.WHITE,
                placeholder: {
                    color: isFemale ? Colors.NEUTRAL_COOL_GREY_400 : Colors.NEUTRAL_COOL_GREY_700,
                    fontWeight: "400",
                },
                focus: {
                    borderColor: isFemale ? Colors.WARM_TANGERINE_500 : Colors.COOL_BLUE_500,
                    background: "transparent",
                },
            },
        },
        submitButton: {
            base: {
                background: isFemale ? Colors.BLACK : Colors.COOL_BLUE_500,
                borderRadius: 16,
                fontSize: "16px",
                lineHeight: "20px",
                fontWeight: "600",
            },

            disabled: {
                //focus: TextStyle & BlockStyle,
            },

            loading: {
                //focus: TextStyle & BlockStyle,
            },
        },
        formSpacings: {
            betweenLabelAndInput: "4px",
            betweenInputs: "10px",
        },
    },
    ...(isApplePayAvailable
        ? {
              applePay: {
                  container: CheckoutContainersIds.Apple,
                  buttonType: "buy",
                  buttonStyle: !isFemale ? "white" : "black",
                  captureBillingAddress: true,
              },
          }
        : {}),
    paypal: {
        buttonColor: "gold",
        buttonShape: "rect",
        buttonSize: "large",
        buttonLabel: "paypal",
        paymentFlow: PaymentFlow.PREFER_VAULT,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        container: CheckoutContainersIds.PayPal,
        buttonHeight: undefined,
        onClick: () => {
            logAnalyticsEvent(WebEventTypes.WEB_BUTTON_CLICK, {
                [WebEventProps.BUTTON_NAME]: "paypal",
                [WebEventProps.METHOD]: "paypal",
                [WebEventProps.SCREEN]: paywallName,
            });
        },
    },
    submitButton: {
        // Callback for receiving the submit button's loading state in the current scene
        onLoading(isLoading, context) {
            //'UniversalCheckout/Home'
            dispatchPayment({
                type: PaymentActions.SET_PAYMENT_PENDING,
                isPaymentPending: isLoading,
            });
            if (context.currentSceneId === "UniversalCheckout/Success/Check") {
                dispatchPayment({
                    type: PaymentActions.SET_PAYMENT_PENDING,
                    isPaymentPending: true,
                });
            }
        },

        // Callback for receiving the submit button's content in the current scene
        onContentChange(content) {
            console.log("content context", content);
        },
    },
    onPaymentMethodAction: (paymentMethodAction, { paymentMethodType }) => {
        if (
            ["PAYMENT_METHOD_UNSELECTED"].includes(paymentMethodAction) &&
            paymentMethodType === "PAYPAL"
        ) {
            dispatchPayment({
                type: PaymentActions.SET_PAYPAL_PAYMENT_LOADING,
                isPaypalPaymentLoading: false,
            });
        }
    },
});

export const getBrainTreeOptions = ({
    isFemale,
    dispatchPayment,
    paywallName,
}: {
    isFemale: boolean;
    paywallName: string;
    dispatchPayment: Dispatch<PaymentAction>;
}) => ({
    createOrder: () => null,
    createPayment: () => {
        dispatchPayment({
            type: PaymentActions.SET_PAYMENT_COMPLETE,
            isPaymentComplete: true,
        });
    },
    onCancel: () => null,
    threeDSecureOnLookupComplete: () => null,
    threeDSecureValidated: () => null,
    onBeforePaymentCreate: () => {
        logAnalyticsEvent(WebEventTypes.PURCHASE_STARTED, {
            [WebEventProps.SCREEN]: paywallName,
            [WebEventProps.METHOD]: "card",
        });
    },
    styles: {
        input: {
            "font-size": "16px",
            "font-family": "Outfit Zing",
            color: isFemale ? Colors.BLACK : Colors.WHITE,
        },
        ":focus": {
            color: isFemale ? Colors.BLACK : Colors.WHITE,
        },
    },
    applePayOptions: {
        merchantCapabilities: ["supports3DS"],
        supportedNetworks: ["visa", "masterCard", "amex", "discover"],
        total: {
            label: "Demo (Card is not charged)",
        },
    },
});
